import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { mutate } from 'swr';
import { CircleNotchIcon, LinkIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ConnectionCard } from '@/components/ConnectionCard';
import {
  ConnectionUpdateDialog,
  type ConnectFlowState,
} from '@/components/ConnectionUpdateDialog';
import { ImportProgress } from '@/components/ImportProgress';
import {
  buildActionRequiredAlert,
  buildErrorAlert,
  buildIdentificationFailedAlert,
  buildTimeoutAlert,
  deriveImportSteps,
} from '@/components/ImportProgress.helpers';
import { hasUsableConnection } from '@/components/onboarding/platformOnboarding';
import { trialDaysLeft } from './flow.helpers';
import {
  clearConnectAttempt,
  consumeConnectAttempt,
  markConnectAttempt,
  promoteConnectAttempt,
  stripConnectReturnMarker,
} from '@/lib/connectAttempt';
import { useItems } from '@/hooks/queries/useItems';
import { useSubscription } from '@/hooks/queries/useSubscription';
import { useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { ApiError, accountsApi, classificationApi, itemsApi, type ItemWithAccounts } from '@/lib/api';
import { track } from '@/lib/telemetry';

const IMPORT_POLL_MS = 3000;
const IMPORT_TIMEOUT_MS = 120_000;

/** Where the post-import group identification stands for the CURRENT pending
 * import. 'idle' = no data landed yet; 'running' = the stream-candidates
 * recompute is in flight; 'done' = the groups are in; 'failed' = the recompute
 * errored — surfaced as a warning with a retry (never silently 'done': with a
 * populated snapshot, Categorias has no failure surface of its own), while
 * Continuar stays usable so an unreachable endpoint cannot trap onboarding. */
type IdentificationPhase = 'idle' | 'running' | 'done' | 'failed';
const ACTIVE_SYNC_STATUSES = ['UPDATING', 'MERGING'];
const ACTION_REQUIRED_STATUSES = ['WAITING_USER_INPUT', 'WAITING_USER_ACTION'];

// How long a connect attempt keeps discovery polling alive — the same budget the
// import stepper gives a started import before declaring a timeout. Measured from
// the RETURN load, not the attempt: a slow bank authorization must not eat the
// window before the user is even back.
const DISCOVERY_WINDOW_MS = IMPORT_TIMEOUT_MS;

interface StepConnectionsProps {
  onContinue: () => void;
  /** A connection was deleted — receives ITS account ids, so the flow prunes
   * exactly that connection's step-2 groups and nothing else. */
  onConnectionRemoved: (accountIds: string[]) => void;
}

/** The Instituições sub-line — it must never promise connections the tier can't
 * add, and never claim the plan allows none when an in-flight import is what
 * fills the cap (the cap counts the raw items list; `connectedCount` excludes a
 * still-importing item). */
function institutionsHint(connectedCount: number, maxConnections: number | null, atCap: boolean): string {
  if (atCap && maxConnections !== null) {
    if (maxConnections === 0) return 'Seu plano atual não permite conexões — faça upgrade para começar.';
    const limit = `seu plano permite ${maxConnections} ${maxConnections === 1 ? 'conexão' : 'conexões'}`;
    if (connectedCount === 0) return `${limit.charAt(0).toUpperCase()}${limit.slice(1)}`;
    return `${connectedCount} conectada${connectedCount > 1 ? 's' : ''} · ${limit}`;
  }
  if (connectedCount === 0) return 'Conecte pelo menos uma para continuar';
  return `${connectedCount} conectada${connectedCount > 1 ? 's' : ''} · dá para adicionar mais depois`;
}

export function StepConnections({ onContinue, onConnectionRemoved }: StepConnectionsProps) {
  const authedFetch = useAuthedFetcher();
  const [connectFlow, setConnectFlow] = useState<ConnectFlowState>({ open: false });
  const [pendingPluggyItemId, setPendingPluggyItemId] = useState<string | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState<ItemWithAccounts | null>(null);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const importing = pendingPluggyItemId !== null;

  // Bounded discovery: polling runs while a connect attempt is live, not for the
  // step's whole life (an idle screen must not poll forever) and not until
  // "something usable exists" (a usable FIRST bank made that stop condition quit
  // before a second bank's item was ever created). The attempt is marked before
  // the Pluggy OAuth hop — sessionStorage plus the URL fallback on the redirect
  // URI for storage-blocked browsers — and consumed one-shot here, so the fresh
  // return load arms exactly one window and an abandoned attempt self-heals
  // (staging, 2026-08-22).
  const [connectAttempt] = useState(consumeConnectAttempt);
  // The reauth/update identity from the consumed attempt: this mount is the
  // RETURN, so the target counts as the expected arrival even though it sits
  // in the baseline (it existed before the attempt).
  const discoveryTarget = connectAttempt.targetItemId;
  const [discoveryDeadline, setDiscoveryDeadline] = useState<number | null>(() =>
    connectAttempt.armed ? Date.now() + DISCOVERY_WINDOW_MS : null,
  );
  const discoveryActive = discoveryDeadline !== null;
  // Which items existed BEFORE the attempt. Broadened adoption (below) may only
  // bind to an item OUTSIDE this set — a pre-existing accountless connection
  // (UPDATED with zero accounts is legal) must not capture the stepper and
  // block the item this attempt actually creates.
  const [discoveryBaseline, setDiscoveryBaseline] = useState<Set<string> | null>(() =>
    connectAttempt.baselineItemIds ? new Set(connectAttempt.baselineItemIds) : null,
  );
  useEffect(() => {
    // The URL carrier was consumed by the state initializer — take it back out
    // of the visible URL (effects own history rewrites, initializers don't).
    stripConnectReturnMarker();
  }, []);
  useEffect(() => {
    if (discoveryDeadline === null) return;
    const expire = () => {
      clearConnectAttempt();
      setDiscoveryDeadline(null);
    };
    const remaining = discoveryDeadline - Date.now();
    if (remaining <= 0) {
      expire();
      return;
    }
    const timer = window.setTimeout(expire, remaining);
    return () => window.clearTimeout(timer);
  }, [discoveryDeadline]);

  const { data: items, isLoading, mutate: refreshItems } = useItems(
    importing || discoveryActive ? IMPORT_POLL_MS : undefined,
  );
  useEffect(() => {
    // A URL-only arm (storage-blocked browser) carries no baseline — treat the
    // first response of this mount as pre-attempt. Declared before the adopt
    // effect so the same response cannot be adopted as fresh.
    if (!discoveryActive || discoveryBaseline !== null || !items) return;
    setDiscoveryBaseline(new Set(items.map((item) => item.id)));
  }, [discoveryActive, discoveryBaseline, items]);
  const navigate = useNavigate();

  // Mirror of the server guard (EntitlementService.AssertCanAddBankConnectionAsync):
  // it counts every non-deleted item — including one mid-import — so the cap reads
  // the raw list, not the `connected` filter below. Entitlements not loaded yet =
  // no cap claimed; the server still refuses if the click beats the fetch.
  const { data: subscription } = useSubscription();
  const maxConnections = subscription?.entitlements.maxBankConnections ?? null;
  const daysLeft = trialDaysLeft(subscription?.trialEndsAt);
  const atConnectionCap = maxConnections !== null && (items ?? []).length >= maxConnections;
  const handleGoUpgrade = useCallback(() => navigate('/profile'), [navigate]);

  const pendingItem = useMemo(
    () =>
      pendingPluggyItemId
        ? (items ?? []).find(
            (item) => item.pluggyItemId === pendingPluggyItemId || item.id === pendingPluggyItemId,
          ) ?? null
        : null,
    [items, pendingPluggyItemId],
  );

  const actionRequired = ACTION_REQUIRED_STATUSES.includes(pendingItem?.status ?? '');
  const loginError = pendingItem?.status === 'LOGIN_ERROR';
  // A blocked item can already carry accounts — completion requires the accounts
  // AND a non-blocked, non-syncing status (mirrors finishPendingImportIfPresent
  // on the Connections page).
  const importCompleted =
    pendingItem !== null &&
    pendingItem.accounts.length > 0 &&
    !ACTIVE_SYNC_STATUSES.includes(pendingItem.status ?? '') &&
    !actionRequired &&
    !loginError;
  const timedOut =
    importing && !importCompleted && startedAt !== null && Date.now() - startedAt > IMPORT_TIMEOUT_MS;

  // Identify the review groups AFTER the data import: the flow's candidates
  // cache was filled before this connection existed (an empty pre-import
  // snapshot), and nothing else refetches it here — so without this recompute
  // the user could hit Continuar and land on a Categorias step that claims
  // there is nothing to review. The stepper shows the phase and Continuar
  // waits for it below.
  const [identification, setIdentification] = useState<IdentificationPhase>('idle');
  useEffect(() => {
    if (importing && importCompleted && identification === 'idle') setIdentification('running');
  }, [importing, importCompleted, identification]);
  useEffect(() => {
    if (identification !== 'running') return;
    let cancelled = false;
    // An explicit fetch, not the hook's bound mutate: a bare SWR revalidation
    // RESOLVES even when its fetch fails (the error goes to the hook state),
    // which silently promoted a failed recompute to 'done'. Success is written
    // into the shared cache BEFORE 'done', so the flow's snapshot merge sees
    // the fresh groups no later than the gate release.
    authedFetch((token) => classificationApi.getStreamCandidates(token))
      .then((fresh) => {
        void mutate(['stream-candidates'], fresh, { revalidate: false });
        if (!cancelled) setIdentification('done');
      })
      .catch(() => {
        if (!cancelled) setIdentification('failed');
      });
    return () => {
      cancelled = true;
    };
  }, [identification, authedFetch]);
  const handleRetryIdentification = useCallback(() => setIdentification('running'), []);
  // A LIVE attempt gates Continuar even while an earlier bank already makes
  // the step passable: walking away mid-import unmounts this component before
  // identification ever runs, so Categorias would open on the old snapshot.
  // Bounded on purpose — a timed-out import and an expired discovery window
  // both release the gate (its alert explains, and blocking onboarding on a
  // stuck bank would be the worse failure).
  const importAttemptActive = (discoveryActive || (importing && !importCompleted)) && !timedOut;
  const identificationPending =
    importing && importCompleted && (identification === 'idle' || identification === 'running');
  const importFullyDone = importCompleted && identification === 'done';
  const identificationStep = importCompleted
    ? identification === 'done'
      ? 'done' as const
      : identification === 'failed'
        ? 'failed' as const
        : 'active' as const
    : 'pending' as const;

  const finishImport = useCallback(() => {
    setPendingPluggyItemId(null);
    setStartedAt(null);
    setLocalError(null);
  }, []);

  // A redirect or reload mid-import loses the local pending state the stepper keys
  // off — the import keeps running server-side while the UI shows no progress and
  // Continuar stays disabled with no explanation. Adopt a still-syncing item as the
  // pending import so the stepper and its polling resume. Safe against re-adoption:
  // a finished import leaves ACTIVE_SYNC status, and the close control is hidden
  // until then.
  useEffect(() => {
    if (pendingPluggyItemId !== null || !items) return;
    // During a live discovery window, an item OUTSIDE the baseline is this
    // attempt's arrival whatever state it landed in — a fast bank can run
    // CREATED → UPDATED-with-accounts between two polls, and requiring
    // zero accounts left the awaiting stepper claiming 'Vinculando conta'
    // next to an already-'Atualizado' card until the window expired
    // (staging, 2026-08-23). Adopting a completed arrival renders the
    // stepper in its done state. Blocked statuses stay unadopted — their
    // card carries the resolve CTA — but they still END the window below:
    // the attempt's outcome is known either way.
    const arrival = discoveryActive && discoveryBaseline !== null
      ? items.find(
          (item) =>
            !discoveryBaseline.has(item.id) ||
            (discoveryTarget !== null &&
              (item.pluggyItemId === discoveryTarget || item.id === discoveryTarget)),
        )
      : undefined;
    const arrivalBlocked =
      arrival !== undefined &&
      (arrival.status === 'LOGIN_ERROR' || ACTION_REQUIRED_STATUSES.includes(arrival.status ?? ''));
    if (arrival && !arrivalBlocked) {
      // The arrival is identified — the window did its job. Ending it here is
      // what keeps the awaiting stepper from reappearing after this import's
      // own close, and stops the polling the window was paying for.
      clearConnectAttempt();
      setDiscoveryDeadline(null);
      setIdentification('idle');
      setPendingPluggyItemId(arrival.pluggyItemId ?? arrival.id);
      setStartedAt(Date.now());
      return;
    }
    if (arrivalBlocked) {
      clearConnectAttempt();
      setDiscoveryDeadline(null);
      return;
    }
    // No arrival to bind — a reload mid-import still resumes a syncing item
    // (the original adoption case). This must NOT end the discovery window:
    // UPDATING/MERGING is also an already-connected bank's routine refresh,
    // and on a second-bank OAuth return that refresh can be adopted before
    // the new webhook item exists — cancelling here would strand the real
    // arrival once this stepper closes.
    const syncing = items.find((item) => ACTIVE_SYNC_STATUSES.includes(item.status ?? ''));
    if (!syncing) return;
    setIdentification('idle');
    setPendingPluggyItemId(syncing.pluggyItemId ?? syncing.id);
    setStartedAt(Date.now());
  }, [discoveryActive, discoveryBaseline, discoveryTarget, items, pendingPluggyItemId]);

  const openConnectFlow = useCallback(
    (item: ItemWithAccounts | null, reason: ConnectFlowState['reason']) => {
      track('action.connect.start', { reason: reason ?? 'onboarding' });
      setLocalError(null);
      // Arm discovery BEFORE the flow opens: the Pluggy OAuth hop navigates this
      // tab away, and the marker is what re-arms the window on the return load.
      // The currently known item ids ride along as the fresh-vs-stale baseline.
      // The stamp carries every known id PLUS the update/reauth target: on the
      // return, the target's post-authorization state resolves the window. The
      // in-memory baseline for THIS mount keeps the full set — nothing has
      // been authorized yet, so the target must not read as an arrival before
      // the hop (an UPDATED target would 'complete' instantly and a
      // LOGIN_ERROR one would kill the window it is about to need).
      const knownItemIds = (items ?? []).map((known) => known.id);
      markConnectAttempt(knownItemIds, item ? item.pluggyItemId ?? item.id : null);
      setDiscoveryBaseline(new Set(knownItemIds));
      setDiscoveryDeadline(Date.now() + DISCOVERY_WINDOW_MS);
      setConnectFlow({
        open: true,
        itemId: item?.pluggyItemId,
        connectorName: item?.connectorName,
        connectorImage: item?.connectorImage,
        reason,
      });
    },
    [items],
  );

  const handleOpenNew = useCallback(() => openConnectFlow(null, 'new'), [openConnectFlow]);
  const handleFlowOpenChange = useCallback((open: boolean) => {
    // Pure state sync: the dialog closes itself on the launch handoff too, so
    // close alone must not read as a cancel — the dialog reports true
    // cancellations through onAbort instead.
    setConnectFlow((current) => ({ ...current, open }));
  }, []);

  const handleConnectLaunched = useCallback(() => {
    // The stamp armed nothing while the dialog was merely open (a reload there
    // must not replay as a return) — the handoff is what makes it a marker.
    promoteConnectAttempt();
  }, []);

  const handleConnectAbort = useCallback(() => {
    // Cancelled before any handover: authorization did not happen, so the
    // awaiting feedback and its polling window must not outlive the attempt
    // claiming it did. After a success this repeats what handleConnectSuccess
    // already did — a no-op. The OAuth hop never gets here: the tab navigates
    // away, which is exactly when the marker must survive.
    clearConnectAttempt();
    setDiscoveryDeadline(null);
  }, []);

  const handleConnectSuccess = useCallback((pluggyItemId: string) => {
    const normalized = pluggyItemId.trim().replace(/^['"]|['"]$/g, '');
    setConnectFlow({ open: false });
    if (!normalized || normalized.toLowerCase() === 'null' || normalized.toLowerCase() === 'undefined') {
      setLocalError('Falha ao completar a conexão. Tente conectar novamente.');
      return;
    }
    track('action.connect.complete', {});
    setLocalError(null);
    // The local (non-OAuth) flow handed over its item — the import stepper's own
    // polling takes it from here.
    clearConnectAttempt();
    setDiscoveryDeadline(null);
    setIdentification('idle');
    setPendingPluggyItemId(normalized);
    setStartedAt(Date.now());
  }, []);

  const handleResolvePending = useCallback(() => {
    if (pendingItem) openConnectFlow(pendingItem, loginError ? 'reauth' : 'update');
  }, [loginError, openConnectFlow, pendingItem]);

  const handleDisconnectRequest = useCallback((item: ItemWithAccounts) => {
    setDisconnecting(item);
  }, []);

  const handleDisconnectCancel = useCallback((open: boolean) => {
    if (!open && !isDisconnecting) setDisconnecting(null);
  }, [isDisconnecting]);

  const handleDisconnectConfirm = useCallback(async () => {
    if (!disconnecting) return;
    setIsDisconnecting(true);
    // The item's EMBEDDED accounts can be incomplete — useItems passes mixed
    // payloads through as-is. Resolve the authoritative list from /accounts by
    // itemId BEFORE the delete (afterwards the server no longer serves them).
    let removedAccountIds = disconnecting.accounts.map((account) => account.id);
    try {
      const allAccounts = await authedFetch((token) => accountsApi.getAll(token));
      removedAccountIds = [...new Set([
        ...removedAccountIds,
        ...allAccounts.filter((account) => account.itemId === disconnecting.id).map((account) => account.id),
      ])];
    } catch {
      if (removedAccountIds.length === 0) {
        // We know NOTHING about this item's accounts — its embedded list is
        // bare and the authoritative read just failed. Deleting now would make
        // the prune a no-op and strand its step-2 groups permanently, so the
        // deletion does not proceed. The dialog stays open for a retry.
        toast.error('Não foi possível remover a conexão agora. Tente novamente.');
        setIsDisconnecting(false);
        return;
      }
      // Embedded ids exist — proceed with them. A non-empty embedded list is a
      // complete one (mixed payloads serve items either fully or bare), so the
      // worst this path can do is nothing at all.
    }
    try {
      await authedFetch((token) => itemsApi.delete(token, disconnecting.id));
      onConnectionRemoved(removedAccountIds);
      setDisconnecting(null);
      void refreshItems();
      // The step-2 totals read this key. Clearing the cached value (not just
      // revalidating) matters: keepPreviousData would otherwise keep serving
      // totals that still include the deleted account while the refetch is in
      // flight — or forever, if it fails — and the user could walk to step 2
      // and confirm those stale numbers. Undefined totals render the card's
      // neutral empty state, so nothing stale is ever confirmable.
      void mutate(['stream-candidates'], undefined, { revalidate: true });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível remover a conexão.');
    } finally {
      setIsDisconnecting(false);
    }
  }, [authedFetch, disconnecting, onConnectionRemoved, refreshItems]);

  // Timeout retry keeps WATCHING: refresh the list and re-arm the window, so an
  // import that finishes late is still observed (never silently dropped).
  const handleTimeoutRetry = useCallback(() => {
    setStartedAt(Date.now());
    void refreshItems();
  }, [refreshItems]);

  const importSteps = importing
    ? deriveImportSteps({
        pendingPluggyItemId,
        pendingItemInDb: pendingItem,
        activeSync: ACTIVE_SYNC_STATUSES.includes(pendingItem?.status ?? ''),
        completed: importCompleted,
        actionRequired,
        errored: loginError || localError !== null,
        timedOut,
        identification: identificationStep,
      })
    : [];

  const importAlert = localError
    ? buildErrorAlert(localError, finishImport)
    : loginError
      ? buildErrorAlert(pendingItem?.errorMessage ?? pendingItem?.errorCode ?? null, handleResolvePending)
      : actionRequired
        ? buildActionRequiredAlert(null, handleResolvePending)
        : timedOut
          ? buildTimeoutAlert(handleTimeoutRetry)
          : identification === 'failed'
            ? buildIdentificationFailedAlert(handleRetryIdentification)
            : undefined;

  // A connection only joins the list when its import finishes — while pending it
  // lives in the stepper card above.
  const connected = (items ?? []).filter(
    (item) => !importing || (item.pluggyItemId !== pendingPluggyItemId && item.id !== pendingPluggyItemId),
  );
  // The gate's strict predicate, not a bare accounts check: a LOGIN_ERROR item
  // with accounts must not enable Continuar — the gate would just bounce the user
  // straight back here after the flow.
  const usableConnection = hasUsableConnection(items ?? []);

  return (
    <section>
      <h1 className="text-[22px] font-bold tracking-tight">Conecte suas contas</h1>
      <p className="mt-1.5 text-sm/6 text-pretty text-text-secondary">
        A Vértice lê seus extratos pelo Open Finance Brasil e organiza tudo sozinha.
        {daysLeft !== null && ` ${daysLeft} ${daysLeft === 1 ? 'dia' : 'dias'} grátis, sem cartão.`}
      </p>

      <div className="mt-6 flex items-end justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[15px] font-bold">Instituições</p>
          <p className="mt-0.5 text-xs text-text-tertiary">
            {institutionsHint(connected.length, maxConnections, atConnectionCap)}
          </p>
        </div>
        {atConnectionCap ? (
          // At the tier cap the honest CTA is the upgrade, not a connect button that
          // can only end in a 402.
          <Button size="sm" variant="outline" onClick={handleGoUpgrade}>
            Fazer upgrade
          </Button>
        ) : (
          <Button size="sm" disabled={importing && !importFullyDone} onClick={handleOpenNew}>
            <PlusIcon size={13} weight="bold" />
            {connected.length === 0 ? 'Conectar conta' : 'Conectar outra'}
          </Button>
        )}
      </div>

      {/* The OAuth return arrives with no local import state: while discovery
          waits for the webhook to land the item, the stepper must already show
          — a silent page after authorizing at the bank reads as a failure. */}
      {!importing && discoveryActive && !connectFlow.open && (
        <div className="mt-3">
          <ImportProgress
            variant="vertical"
            steps={deriveImportSteps({
              pendingPluggyItemId: 'oauth-return',
              pendingItemInDb: null,
              activeSync: false,
              completed: false,
              actionRequired: false,
              errored: false,
              timedOut: false,
              identification: 'pending',
            })}
            hideClose
          />
        </div>
      )}

      {importing && (
        <div className="mt-3">
          <ImportProgress
            variant="vertical"
            steps={importSteps}
            alert={importAlert}
            connectorName={pendingItem?.connectorName ?? connectFlow.connectorName}
            connectorImage={pendingItem?.connectorImage ?? connectFlow.connectorImage}
            onClose={importFullyDone ? finishImport : undefined}
            hideClose={!importFullyDone}
          />
        </div>
      )}

      {/* A failed launch (e.g. Pluggy returned no item id) never reaches the
          importing stepper — surface it here so the user can retry with feedback. */}
      {!importing && localError && (
        <Alert variant="destructive" className="mt-3">
          {localError}
        </Alert>
      )}

      {isLoading && !importing && connected.length === 0 && (
        <div className="mt-3 flex flex-col gap-3">
          <Skeleton className="h-24 w-full rounded-3xl" />
        </div>
      )}

      {!isLoading && connected.length === 0 && !importing && !discoveryActive && (
        <div className="mt-3 grid min-h-24 place-items-center gap-1.5 rounded-2xl border border-dashed border-border-subtle bg-surface px-5 py-8">
          <LinkIcon size={26} weight="duotone" className="text-text-tertiary" />
          <p className="text-sm text-text-tertiary">Nenhuma instituição conectada</p>
          <p className="text-xs text-text-tertiary">Comece pelo banco onde cai o seu salário.</p>
        </div>
      )}

      <div className="mt-3 flex flex-col gap-3">
        {connected.map((item) => (
          <OnboardingConnectionCard
            key={item.id}
            item={item}
            onFlow={openConnectFlow}
            onDisconnectRequest={handleDisconnectRequest}
          />
        ))}
      </div>

      <ConnectionUpdateDialog
        state={connectFlow}
        importState={{ active: false, steps: [] }}
        onOpenChange={handleFlowOpenChange}
        onSuccess={handleConnectSuccess}
        onAbort={handleConnectAbort}
        onLaunched={handleConnectLaunched}
      />

      <Dialog open={disconnecting !== null} onOpenChange={handleDisconnectCancel}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Remover conexão</DialogTitle>
            <DialogDescription>
              {disconnecting
                ? `Você vai remover a conexão com ${disconnecting.connectorName}. Você poderá reconectar depois, mas os dados deixam de atualizar até lá.`
                : 'Confirme para remover esta conexão.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="destructive" disabled={isDisconnecting} onClick={handleDisconnectConfirm}>
              {isDisconnecting ? <CircleNotchIcon size={16} className="animate-spin" /> : <TrashIcon size={16} />}
              Remover
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="mt-6 flex items-center justify-end gap-3 border-t border-border-subtle pt-4">
        <Button disabled={!usableConnection || importAttemptActive || identificationPending} onClick={onContinue}>
          Continuar
        </Button>
      </div>
      {(!usableConnection || importAttemptActive || identificationPending) && (
        <p aria-live="polite" className="mt-2.5 text-right text-xs text-text-tertiary">
          {!usableConnection
            ? 'Conecte uma instituição para continuar.'
            : importAttemptActive
              ? 'Aguarde a importação terminar para continuar.'
              : 'Identificando os grupos para a sua revisão…'}
        </p>
      )}
    </section>
  );
}

/** Binds the shared ConnectionCard's action callbacks to this item. */
function OnboardingConnectionCard({
  item,
  onFlow,
  onDisconnectRequest,
}: {
  item: ItemWithAccounts;
  onFlow: (item: ItemWithAccounts, reason: ConnectFlowState['reason']) => void;
  onDisconnectRequest: (item: ItemWithAccounts) => void;
}) {
  const handleReauth = useCallback(() => onFlow(item, 'reauth'), [onFlow, item]);
  const handleRenew = useCallback(() => onFlow(item, 'renew'), [onFlow, item]);
  const handleUpdate = useCallback(() => onFlow(item, 'update'), [onFlow, item]);
  const handleDisconnect = useCallback(() => onDisconnectRequest(item), [onDisconnectRequest, item]);

  return (
    <ConnectionCard
      item={item}
      onReauth={handleReauth}
      onRenew={handleRenew}
      onUpdate={handleUpdate}
      onDisconnect={handleDisconnect}
    />
  );
}
